import {
  useInfiniteQuery,
  type RefetchOptions,
  type QueryObserverResult,
} from "@tanstack/react-query";
import lodashIsNumber from "lodash/fp/isNumber";
import lodashOmit from "lodash/fp/omit";
import type React from "react";
import { useMemo, useEffect } from "react";
import {
  type PillarApiError,
  toPillarApiError,
} from "shared/api/PillarApiError";
import {
  type PaginatedResults,
  type PaginationRequest,
} from "shared/api/types/pagination";
import {
  PaginationType,
  type DataQueryFunction,
} from "shared/components/pillar-table/PillarTableContext";
import type { PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { type FiltersRequest } from "shared/filter-where-clause";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

export interface UsePillarTableQueryValuesOptions<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> {
  queryKey?: string | string[];
  dataQuery?: DataQueryFunction<RowGeneric, ExtraPropsGeneric>;
  filtersRequest: FiltersRequest;
  filtersRendered: boolean;
  setFilters: (incomingFilters: FiltersRequest) => void;
  pagination: boolean | PaginationType;
  paginationState: PaginationRequest | undefined;
  setPaginationState: React.Dispatch<
    React.SetStateAction<PaginationRequest | undefined>
  >;
}

export type UsePillarTableQueryValuesResult<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  results: PaginatedResults<RowGeneric, ExtraPropsGeneric>["results"];
  latestPageData?: PaginatedResults<RowGeneric, ExtraPropsGeneric>;
  /** What the last attempt at `dataQuery` failed with, if it failed. */
  dataQueryError?: PillarApiError;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isRefetching: boolean;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<PaginatedResults<RowGeneric, ExtraPropsGeneric>>
  >;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isFetchingPreviousPage: boolean;
  isFetchingNextPage: boolean;
  fetchPreviousPage: () => void;
  fetchNextPage: () => void;
};

export function usePillarTableQueryValues<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>(
  options: UsePillarTableQueryValuesOptions<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >,
): UsePillarTableQueryValuesResult<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> {
  const {
    queryKey,
    dataQuery,
    filtersRequest,
    filtersRendered,
    paginationState,
    pagination,
    setPaginationState,
  } = options;

  const isInfinityPagination = pagination === PaginationType.Infinity;

  const {
    data,
    error,
    isError,
    isLoading,
    isFetching,
    isFetched,
    isRefetching,
    refetch: queryRefetch,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage: queryFetchPreviousPage,
    fetchNextPage: queryFetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "pillarTable",
      ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
      filtersRequest,
      isInfinityPagination
        ? lodashOmit("page", paginationState)
        : paginationState,
      filtersRendered,
    ],
    queryFn: async ({ pageParam }) => {
      if (!filtersRendered || !dataQuery) {
        return {
          results: [],
          totalResults: 0,
          pageSize: 0,
          page: 0,
          dynamicFilterChoices: {},
        };
      }
      return dataQuery(
        filtersRequest,
        isInfinityPagination
          ? {
              ...paginationState,
              page: lodashIsNumber(pageParam) ? pageParam : DEFAULT_PAGE,
              pageSize: paginationState?.pageSize || DEFAULT_PAGE_SIZE,
            }
          : paginationState,
      );
    },
    getPreviousPageParam: (firstPageData) => {
      // For regular pagination, never return previous page param
      if (!isInfinityPagination || !firstPageData) {
        return;
      }
      const previousPage = Math.max(firstPageData.page - 1, 1);
      return previousPage !== firstPageData.page ? previousPage : undefined;
    },
    getNextPageParam: (lastPageData) => {
      // For regular pagination, never return next page param
      if (!isInfinityPagination || !lastPageData) {
        return;
      }
      const totalPageCount = Math.max(
        1,
        Math.ceil(lastPageData.totalResults / lastPageData.pageSize),
      );
      const nextPage = Math.min(lastPageData.page + 1, totalPageCount);
      return nextPage !== lastPageData.page ? nextPage : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    networkMode: "always",
  });

  const pages = data?.pages;
  const latestPageData = pages?.[pages.length - 1];
  const dataQueryError = useMemo(
    () => (isError ? toPillarApiError(error) : undefined),
    [isError, error],
  );
  const results = useMemo(() => {
    return pages?.flatMap((page) => page?.results ?? []) ?? [];
  }, [pages]);

  // Sync local pagination state with infinite query's current page
  useEffect(() => {
    if (
      !isInfinityPagination ||
      !latestPageData ||
      !paginationState ||
      latestPageData.page === paginationState.page
    ) {
      return;
    }

    setPaginationState({ ...paginationState, page: latestPageData.page });
  }, [latestPageData?.page]);

  const refetch: UsePillarTableQueryValuesResult<
    RowGeneric,
    ExtraPropsGeneric
  >["refetch"] = async (options) => {
    const { data, ...rest } = await queryRefetch(options);
    const pages = data?.pages;
    const latestPage = pages?.[pages.length - 1] as PaginatedResults<
      RowGeneric,
      ExtraPropsGeneric
    >;
    return {
      ...rest,
      data: latestPage,
    } as QueryObserverResult<PaginatedResults<RowGeneric, ExtraPropsGeneric>>;
  };

  const fetchPreviousPage = () => {
    if (!isInfinityPagination || !hasPreviousPage || isFetchingPreviousPage) {
      return;
    }

    queryFetchPreviousPage();
  };

  const fetchNextPage = () => {
    if (!isInfinityPagination || !hasNextPage || isFetchingNextPage) {
      return;
    }

    queryFetchNextPage();
  };

  return {
    results,
    latestPageData,
    dataQueryError,
    isLoading,
    isFetching,
    isFetched,
    isRefetching,
    refetch,
    hasPreviousPage: !!hasPreviousPage,
    hasNextPage: !!hasNextPage,
    isFetchingPreviousPage: isInfinityPagination
      ? isFetchingPreviousPage
      : false,
    isFetchingNextPage: isInfinityPagination ? isFetchingNextPage : false,
    fetchPreviousPage,
    fetchNextPage,
  };
}
